import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { DarkUpperSection, LoadingSpinner, PageSection } from '../../components';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ManifestManagement from './components/ManifestManagement';
import SSCCManagement from './components/SSCCManagement';
import ReportingLogs from './components/ReportingLogs';
import { useUserDetails } from '../../contexts/UserDetailsContext';
import ASNManagement from './components/ASNManagement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      style={{ backgroundColor: 'background.default' }}
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const TAB_KEYS = ['manifests', 'ssccs', 'asns', 'reports'] as const;
type TabKey = (typeof TAB_KEYS)[number];

export default function AdminPanel() {
  const theme = useTheme();
  const { userDetails, isLoading: isLoadingUser } = useUserDetails();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get('tab') as TabKey | null;
  const initialTabIndex = Math.max(TAB_KEYS.indexOf(tabFromUrl ?? 'manifests'), 0);

  const [currentTab, setCurrentTab] = useState(initialTabIndex);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setSearchParams({ tab: TAB_KEYS[newValue] });
  };

  // Keep state in sync if URL changes externally (back/forward)
  useEffect(() => {
    if (tabFromUrl) {
      const index = TAB_KEYS.indexOf(tabFromUrl);
      if (index !== -1 && index !== currentTab) {
        setCurrentTab(index);
      }
    }
  }, [tabFromUrl, currentTab]);
  if (isLoadingUser) {
    return <LoadingSpinner isLoading={true} children={[]} />;
  }
  if (!userDetails?.adminPanelAccess) {
    return null;
  }

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={2} sx={{ padding: '1em 0' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <AdminPanelSettingsIcon sx={{ fontSize: 32, color: 'primary.contrastText' }} />
            <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
              Admin Panel
            </Typography>
          </Stack>
          <Typography variant="body1" color={theme.palette.primary.contrastText}>
            Manage manifests, SSCCs, and view system logs
          </Typography>
        </Stack>
      </DarkUpperSection>
      <Box
        sx={{
          position: 'sticky',
          top: 178.04,
          zIndex: 20,
          backgroundColor: 'background.default'
        }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.default',
            borderRadius: '8px 8px 0 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}>
          <PageSection sx={{ mt: 0, pt: 0 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="admin panel tabs"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  minHeight: 64,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500
                }
              }}>
              <Tab icon={<LocalShippingIcon />} iconPosition="start" label="Manifest Management" sx={{ gap: 1 }} />
              <Tab icon={<InventoryIcon />} iconPosition="start" label="SSCC Management" sx={{ gap: 1 }} />
              <Tab icon={<DescriptionIcon />} iconPosition="start" label="ASN Management" sx={{ gap: 1 }} />
              <Tab icon={<AssessmentIcon />} iconPosition="start" label="Reporting & Logs" sx={{ gap: 1 }} />
            </Tabs>
          </PageSection>
        </Box>
      </Box>
      <PageSection sx={{ mt: 1 }}>
        <Box
          sx={{
            backgroundColor: 'grey.50',
            borderRadius: '0 0 8px 8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            minHeight: '60vh'
          }}>
          <TabPanel value={currentTab} index={0}>
            <ManifestManagement />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <SSCCManagement />
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <ASNManagement />
          </TabPanel>
          <TabPanel value={currentTab} index={3}>
            <ReportingLogs />
          </TabPanel>
        </Box>
      </PageSection>
    </>
  );
}
